import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchListing, submitEnquiry } from "../api/marketingApi";
import { EnquiryForm } from "../components/EnquiryForm";
import { ExitIntent } from "../components/ExitIntent";
import { trackVisit } from "../hooks/useVisitor";
import { trackViewContent } from "../lib/metaPixel";

const STATE_LABELS = { new: "New", rental: "For Rent", resale: "Resale" };

export function MarketingListing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    fetchListing(token)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setStatus("ok");
        trackVisit({ listingToken: token }); // anonymous view beacon (no PII)
        trackViewContent({ content_name: d.headline, content_category: d.offer_state }); // Meta ViewContent
      })
      .catch(() => alive && setStatus("notfound"));
    return () => {
      alive = false;
    };
  }, [token]);

  if (status === "loading") {
    return <div className="mx-auto max-w-[68rem] px-6 py-24 font-sans text-[#78746A]">Loading…</div>;
  }
  if (status === "notfound") {
    return (
      <div className="mx-auto max-w-[34rem] px-6 py-24 text-center">
        <h1 className="text-[2rem] font-semibold">This property isn't available</h1>
        <p className="mt-3 text-[1.05rem] text-[#4A473E]">
          The link may have expired or the listing has been taken down.
        </p>
        <Link to="/properties" className="mt-6 inline-block font-sans font-semibold text-[#2C4A87]">
          Browse other properties →
        </Link>
      </div>
    );
  }

  const loc = [data.locality, data.city].filter(Boolean).join(", ");
  const photos = data.photos || [];
  const chips = [
    STATE_LABELS[data.offer_state] || data.offer_state,
    data.configuration,
    data.bedrooms ? `${data.bedrooms} Bed` : null,
    data.bathrooms ? `${data.bathrooms} Bath` : null,
    data.area_sqft ? `${data.area_sqft} sq.ft` : null,
    data.furnishing ? cap(data.furnishing.replace(/_/g, " ")) : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-[68rem] px-6 pb-20 pt-8">
      {photos[0] && (
        <button type="button" onClick={() => setLightbox(0)} className="block w-full">
          <img src={photos[0].large} alt="" className="aspect-video w-full rounded-2xl object-cover" />
        </button>
      )}

      <div className="mt-6">
        <span className="font-sans text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-[#B7975A]">
          {STATE_LABELS[data.offer_state] || data.offer_state}
          {data.property_type ? ` · ${cap(data.property_type)}` : ""}
        </span>
        <h1 className="mt-2 text-balance text-[clamp(1.8rem,4.5vw,2.7rem)] font-semibold leading-tight tracking-tight">
          {data.headline}
        </h1>
        {loc && <p className="mt-1 text-[1.05rem] text-[#4A473E]">{loc}</p>}
        {data.price_display && (
          <div className="mt-3 font-sans text-[1.5rem] font-bold text-[#1F3766]">
            {data.price_display}
            {data.price_negotiable && <span className="ml-2 text-[0.8rem] font-medium text-[#78746A]">· Negotiable</span>}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((c, i) => (
            <span key={i} className="rounded-full border border-[#DED9CC] bg-[#EDEAE2] px-[0.7rem] py-[0.32rem] font-sans text-[0.78rem] text-[#4A473E]">
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-12">
        <div>
          {/* Extra gallery thumbnails */}
          {photos.length > 1 && (
            <div className="mb-6 grid grid-cols-3 gap-2">
              {photos.slice(1).map((p, i) => (
                <button key={i} type="button" onClick={() => setLightbox(i + 1)}>
                  <img src={p.thumb} alt="" className="aspect-[4/3] w-full rounded-lg object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Description — pre-line keeps the line breaks the broker typed. Inline
              style because the whitespace-pre-line utility isn't in the
              precompiled Tailwind build. */}
          {data.description && (
            <p
              className="mb-6 text-[1.05rem] leading-relaxed text-[#4A473E]"
              style={{ whiteSpace: "pre-line" }}
            >
              {data.description}
            </p>
          )}

          {/* Configurable blocks */}
          {(data.blocks || []).map((b, i) => (
            <Block key={i} block={b} photos={photos} />
          ))}

          {/* Video / map embeds */}
          {data.video_embeds?.length > 0 && (
            <div className="mt-6 space-y-4">
              {data.video_embeds.map((src, i) => (
                <iframe
                  key={i}
                  src={src}
                  title={`Media ${i + 1}`}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="aspect-video w-full rounded-xl border-0"
                />
              ))}
            </div>
          )}

          {/* Handover timeline */}
          {(data.launch_date || data.possession_date) && (
            <div className="mt-6 flex flex-wrap gap-6 font-sans text-[0.9rem]">
              {data.launch_date && <TimelineItem label="Launched" value={fmtDate(data.launch_date)} />}
              {data.possession_date && <TimelineItem label="Possession" value={fmtDate(data.possession_date)} />}
            </div>
          )}

          {data.amenities?.length > 0 && (
            <>
              <h2 className="mt-8 text-[1.45rem] font-semibold">Amenities</h2>
              <ul className="mt-3 grid list-none grid-cols-2 gap-2 p-0 font-sans text-[0.9rem] text-[#4A473E] sm:grid-cols-3">
                {data.amenities.map((a, i) => (
                  <li key={i} className="relative pl-5 before:absolute before:left-0 before:font-bold before:text-[#B7975A] before:content-['✓']">
                    {a}
                  </li>
                ))}
              </ul>
            </>
          )}

          {data.tags?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {data.tags.map((t, i) => (
                <span key={i} className="rounded-full bg-[#EDEAE2] px-3 py-1 font-sans text-[0.78rem] text-[#4A473E]">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="rounded-2xl border border-[#DED9CC] bg-[#FBFAF6] p-6 shadow-[0_1px_2px_rgba(20,19,15,0.04),0_12px_30px_rgba(20,19,15,0.05)] lg:sticky lg:top-24">
            <h2 className="text-[1.25rem] font-semibold">Get a quote for this property</h2>
            <p className="mb-4 mt-1 font-sans text-[0.9rem] text-[#4A473E]">
              Share your details and our property partner will send you a quote shortly.
            </p>
            <EnquiryForm token={data.token} onSubmit={(payload) => submitEnquiry(data.token, payload)} />
          </div>
        </div>
      </div>

      {/* Lightbox with prev/next + swipe navigation */}
      <AnimatePresence>
        {lightbox !== null && photos[lightbox] && (
          <Lightbox
            photos={photos}
            index={lightbox}
            onIndex={setLightbox}
            onClose={() => setLightbox(null)}
          />
        )}
      </AnimatePresence>

      <ExitIntent token={data.token} listingLabel={data.headline} />
    </div>
  );
}

// Full-screen photo viewer with prev/next buttons, keyboard arrows, Escape, and
// touch-swipe (mobile). Wraps around at both ends when there is more than one.
function Lightbox({ photos, index, onIndex, onClose }) {
  const count = photos.length;
  const go = useCallback(
    (delta) => onIndex((i) => (i + delta + count) % count),
    [onIndex, count]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  // Touch swipe: horizontal drag past a threshold moves to the next/prev photo.
  const touchX = useRef(null);
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40 && count > 1) go(dx < 0 ? 1 : -1);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-2xl leading-none text-white backdrop-blur hover:bg-white/25"
      >
        ×
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous photo"
            className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 sm:left-6"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next photo"
            className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur hover:bg-white/25 sm:right-6"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>

          {/* Counter */}
          <div className="absolute bottom-5 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white/15 px-3 py-1 font-sans text-[0.8rem] text-white backdrop-blur">
            {index + 1} / {count}
          </div>
        </>
      )}

      <img
        key={index}
        src={photos[index].large}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] select-none rounded-lg object-contain"
      />
    </motion.div>
  );
}

function Block({ block, photos }) {
  const props = block.props || {};
  switch (block.type) {
    case "heading": {
      const size = props.level === 1 ? "text-[1.9rem]" : props.level === 3 ? "text-[1.15rem]" : "text-[1.45rem]";
      return <div className={`mb-2 mt-4 font-semibold ${size}`}>{props.text}</div>;
    }
    case "text":
      return <div className="prose prose-lg mb-4 max-w-none text-[#14130F]" dangerouslySetInnerHTML={{ __html: props.html || "" }} />;
    case "spec_grid": {
      const items = (props.items || []).filter((i) => i && i.value);
      if (!items.length) return null;
      return (
        <div className="my-4 grid gap-4 rounded-2xl border border-[#DED9CC] bg-[#FBFAF6] p-5 [grid-template-columns:repeat(auto-fit,minmax(130px,1fr))]">
          {items.map((it, i) => (
            <div key={i}>
              <div className="font-sans text-[0.7rem] uppercase tracking-[0.08em] text-[#78746A]">{it.label}</div>
              <div className="mt-0.5 text-[1.05rem] font-semibold">{it.value}</div>
            </div>
          ))}
        </div>
      );
    }
    case "divider":
      return <hr className="my-6 border-[#DED9CC]" />;
    default:
      return null;
  }
}

function cap(s) {
  return String(s).charAt(0).toUpperCase() + String(s).slice(1);
}

function TimelineItem({ label, value }) {
  return (
    <div>
      <div className="text-[0.7rem] uppercase tracking-[0.08em] text-[#78746A]">{label}</div>
      <div className="mt-0.5 font-semibold text-[#14130F]">{value}</div>
    </div>
  );
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
